import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static targets = [
    "panel", "backdrop", "title", "submitLabel", "deleteBtn", "dirtyNotice",
    "name", "visibility", "sharedFields", "sharedSelect", "colorOption"
  ]

  static values = {
    createUrl:         String,
    membresUrl:        String,
    updateUrlTemplate: String,
    resourceType:      { type: String, default: "Etude" },
    defaultSortField:  { type: String, default: "ref_etude" },
    defaultGroupBy:    { type: String, default: "statut" }
  }

  connect() {
    const sel = this.sharedSelectTarget
    if (sel.tomselect) {
      this.tomSelect = sel.tomselect
      this._owns = false
    } else {
      this.tomSelect = new TomSelect(sel, {
        valueField: "id", labelField: "label", searchField: ["label"],
        plugins: ["remove_button"], persist: false, create: false,
        placeholder: "Rechercher des membres…",
        load: (q, cb) => {
          fetch(`${this.membresUrlValue}?q=${encodeURIComponent(q || "")}&limit=30`, { headers: { Accept: "application/json" } })
            .then(r => r.json()).then(cb).catch(() => cb())
        }
      })
      this._owns = true
    }
    this.editing = null
    this._setColor("#4f46e5")
  }

  disconnect() {
    if (this._owns) this.tomSelect?.destroy()
  }

  // ---- triggers ---------------------------------------------------------

  openNew() {
    this.editing = null
    this.titleTarget.textContent = "Créer une vue"
    this.submitLabelTarget.textContent = "Créer la vue"
    this.deleteBtnTarget.style.display = "none"
    this.nameTarget.value = ""
    this.visibilityTargets.forEach(r => { r.checked = (r.value === "private") })
    this.sharedFieldsTarget.style.display = "none"
    this.tomSelect.clear(true)
    this._setColor("#4f46e5")
    if (this.hasDirtyNoticeTarget) this.dirtyNoticeTarget.hidden = true
    this._open()
  }

  openSave(event) {
    // If we're already on a saved view that the user can edit, prefill for update.
    const idx = window.__listIndex || window.__etudesIndex
    const view = idx?.currentViewValue
    const numericId = view && /^\d+$/.test(view) ? parseInt(view, 10) : null

    if (numericId) {
      this._loadForEdit(numericId)
    } else {
      this.openNew()
    }
  }

  pickColor(event) {
    this._setColor(event.currentTarget.dataset.color)
  }

  changeVisibility(event) {
    this.sharedFieldsTarget.style.display = event.target.value === "shared" ? "" : "none"
  }

  close() {
    this.panelTarget.classList.remove("is-open")
    this.backdropTarget.classList.remove("is-open")
  }

  // ---- submit / delete --------------------------------------------------

  async submit() {
    const name = this.nameTarget.value.trim()
    if (!name) {
      this.nameTarget.focus()
      return
    }

    const visibility = this.visibilityTargets.find(r => r.checked)?.value || "private"
    const config = this._buildConfig()
    const payload = {
      saved_view: {
        name,
        color: this._currentColor,
        visibility,
        resource_type: this.resourceTypeValue,
        config: JSON.stringify(config),
        shared_with_ids: visibility === "shared" ? this.tomSelect.getValue() : []
      }
    }

    const isUpdate = !!this.editing
    const url = isUpdate
      ? this.updateUrlTemplateValue.replace("__id__", this.editing.id)
      : this.createUrlValue

    const res = await fetch(url, {
      method: isUpdate ? "PATCH" : "POST",
      headers: this._headers(),
      body: JSON.stringify(payload)
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      alert(data.errors?.join(", ") || "Erreur lors de l'enregistrement")
      return
    }

    const view = await res.json()
    // Navigate to the new view so the user sees it immediately.
    const url2 = new URL(window.location.href)
    url2.searchParams.set("view_id", view.id)
    // Strip every override param — they've all just been baked into the view's
    // stored config, so leaving them in the URL would keep the "Modifications
    // non enregistrées" pill on after a successful save (bug we hit before).
    const toDelete = []
    url2.searchParams.forEach((_v, k) => {
      if (
        k === "filters" ||
        k.startsWith("filters[") ||
        k === "view_type" ||
        k === "group_by" ||
        k === "sort_field" ||
        k === "sort_direction" ||
        k === "dashboard_widgets" ||
        k === "columns" ||
        k === "columns[]" ||
        k === "frozen_count" ||
        k === "page" ||
        k === "per_page"
      ) toDelete.push(k)
    })
    toDelete.forEach(k => url2.searchParams.delete(k))
    window.location.href = url2.toString()
  }

  async delete() {
    if (!this.editing) return
    if (!confirm("Supprimer cette vue ?")) return

    const url = this.updateUrlTemplateValue.replace("__id__", this.editing.id)
    const res = await fetch(url, { method: "DELETE", headers: this._headers() })
    if (res.ok) {
      const u = new URL(window.location.href)
      u.searchParams.set("view_id", "mine")
      window.location.href = u.toString()
    } else {
      alert("Erreur lors de la suppression")
    }
  }

  // ---- internals --------------------------------------------------------

  _open() {
    this.panelTarget.classList.add("is-open")
    this.backdropTarget.classList.add("is-open")
  }

  async _loadForEdit(id) {
    this.editing = { id }
    this.titleTarget.textContent = "Modifier la vue"
    this.submitLabelTarget.textContent = "Mettre à jour"
    this.deleteBtnTarget.style.display = ""

    const idx = window.__listIndex || window.__etudesIndex
    const cfg = idx?.configValue || {}
    const url = cfg.saved_views_url
    if (!url) { this._open(); return }

    // The saved_views index endpoint is shared between resources — pass the
    // current resource_type so the backend filters correctly (defaults to Etude).
    const sep = url.includes("?") ? "&" : "?"
    const fullUrl = `${url}${sep}resource_type=${encodeURIComponent(this.resourceTypeValue)}`
    const list = await fetch(fullUrl, { headers: { Accept: "application/json" } }).then(r => r.json()).catch(() => [])
    const view = list.find(v => String(v.id) === String(id))
    if (!view) { this._open(); return }

    this.editing = view
    this.nameTarget.value = view.name || ""
    this._setColor(view.color || "#4f46e5")
    this.visibilityTargets.forEach(r => { r.checked = (r.value === view.visibility) })
    this.sharedFieldsTarget.style.display = view.visibility === "shared" ? "" : "none"
    if (this.hasDirtyNoticeTarget) this.dirtyNoticeTarget.hidden = !this._isDirty()
    this.tomSelect.clear(true)
    if (view.shared_with_ids?.length) {
      const u = `${this.membresUrlValue}?ids[]=${view.shared_with_ids.join("&ids[]=")}`
      const items = await fetch(u, { headers: { Accept: "application/json" } }).then(r => r.json()).catch(() => [])
      items.forEach(it => this.tomSelect.addOption(it))
      this.tomSelect.setValue(view.shared_with_ids.map(String), true)
    }
    this._open()
  }

  _buildConfig() {
    // Read the live URL state — view_type, group_by, sort, columns, filters —
    // and serialize it into the saved-view JSON config.
    const url = new URL(window.location.href)
    const idx = window.__listIndex || window.__etudesIndex
    const columns = url.searchParams.getAll("columns[]")
    const frozen  = url.searchParams.get("frozen_count")
    const config = {
      view_type: url.searchParams.get("view_type") || (idx?.viewTypeValue ?? "list"),
      group_by:  url.searchParams.get("group_by")  || idx?.groupByValue || this.defaultGroupByValue,
      sort: {
        field:     url.searchParams.get("sort_field") || this.defaultSortFieldValue,
        direction: url.searchParams.get("sort_direction") || "desc"
      },
      filters: idx?.filtersValue || {}
    }
    if (columns.length) config.columns = columns
    if (frozen)         config.frozen_count = parseInt(frozen, 10)

    // Dashboard widgets — read from URL param (virtual view) or from the live
    // dashboard controller's state (saved view).
    if (config.view_type === "dashboard") {
      const raw = url.searchParams.get("dashboard_widgets")
      if (raw) {
        try { config.widgets = JSON.parse(raw) } catch (_) { /* ignored */ }
      }
      if (!config.widgets) {
        const dashEl = document.querySelector('[data-controller~="dashboard"]')
        const wRaw = dashEl?.getAttribute("data-dashboard-widgets-value")
        if (wRaw) {
          try { config.widgets = JSON.parse(wRaw) } catch (_) { /* ignored */ }
        }
      }
    }
    return config
  }

  // The page is "dirty" relative to the saved view's stored config when the
  // URL carries any of the override params (filters, view_type, sort, columns…).
  // We check the live URL rather than ask the server because save_view_modal is
  // a thin client component and doesn't know the saved config.
  _isDirty() {
    const url = new URL(window.location.href)
    const overrideParams = ["view_type", "group_by", "sort_field", "sort_direction", "frozen_count", "dashboard_widgets"]
    if (overrideParams.some(k => url.searchParams.has(k))) return true
    let hit = false
    url.searchParams.forEach((_v, k) => {
      if (k.startsWith("filters[") || k === "columns[]") hit = true
    })
    return hit
  }

  _setColor(c) {
    this._currentColor = c
    this.colorOptionTargets.forEach(btn => {
      btn.classList.toggle("is-picked", btn.dataset.color === c)
    })
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": csrf || ""
    }
  }
}
